import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requestJson } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

export type CustomerAddress = {
  id: string;
  userId: string;
  label: string;
  line1: string;
  line2: string | null;
  city: string | null;
  country: string;
  lat: string;
  lng: string;
  notes: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CustomerAddressInput = {
  label: string;
  line1: string;
  line2?: string | null;
  city?: string | null;
  country: string;
  lat: number;
  lng: number;
  notes?: string | null;
  isDefault?: boolean;
};

type ListResponse = { addresses: CustomerAddress[] };
type SingleResponse = { address: CustomerAddress };

export function useCustomerAddresses(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.customerAddresses.all(),
    queryFn: () => requestJson<ListResponse>("/api/customer-addresses"),
    enabled: options?.enabled ?? true,
    select: (data) => data.addresses,
  });
}

export function useCreateCustomerAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomerAddressInput) =>
      requestJson<SingleResponse>("/api/customer-addresses", {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerAddresses.all() });
    },
  });
}

export function useUpdateCustomerAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CustomerAddressInput> }) =>
      requestJson<SingleResponse>(`/api/customer-addresses/${id}`, {
        method: "PUT",
        headers: JSON_HEADERS,
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerAddresses.all() });
    },
  });
}

export function useDeleteCustomerAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      requestJson<{ ok: boolean }>(`/api/customer-addresses/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerAddresses.all() });
    },
  });
}

export function useSetDefaultCustomerAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      requestJson<SingleResponse>(`/api/customer-addresses/${id}/set-default`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerAddresses.all() });
    },
  });
}
